import React, { useState, useEffect, useCallback } from 'react';
import { Search, ArrowRight, X } from 'lucide-react';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';

const PAGES = [
  { key: 'dashboard', label: 'Dashboard', desc: 'Welkomstscherm, maandtaken, exports' },
  { key: 'tw1-memo', label: 'Truckwash 1 Memo', desc: 'AI memoriaalboekingen chatbot' },
  { key: 'truckshop-memo', label: 'Truckshop 1 Memo', desc: 'Voorraadmutatie en omzetverdeling' },
  { key: 'maandafsluiting', label: 'Maandafsluiting', desc: 'Omzet, fee, doorbelasting, Exact exports' },
  { key: 'ptw-import', label: 'PTW Fac. Import', desc: 'Factuurbatches splitsen per entiteit' },
  { key: 'incassos', label: "Incasso's Exact", desc: 'Incassobatches klaarzetten' },
  { key: 'betalingen', label: 'Betalingen Exact', desc: 'Leveranciersbetalingen per entiteit' },
  { key: 'salaris', label: 'Salaris', desc: 'SEPA-batches voor salarisbetalingen' },
  { key: 'sepa', label: 'SEPA Betaalbatch', desc: 'pain.001 XML voor ING Zakelijk' },
  { key: 'storno', label: 'Storno Incasso', desc: 'Terugboekingen en re\u00efncasso' },
  { key: 'logboek', label: 'Logboek', desc: 'Overzicht alle exports' },
  { key: 'instellingen', label: 'Instellingen', desc: 'Gebruikers, locaties, account' },
];

export default function GlobalSearch({ onNavigate }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const handleKeyDown = useCallback((e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      setOpen(true);
      setQuery('');
    }
    if (e.key === 'Escape') setOpen(false);
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const filtered = query
    ? PAGES.filter(p => p.label.toLowerCase().includes(query.toLowerCase()) || p.desc.toLowerCase().includes(query.toLowerCase()))
    : PAGES;

  const handleSelect = (key) => {
    onNavigate(key);
    setOpen(false);
  };

  return (
    <>
      {/* Search trigger in header */}
      <button
        data-testid="global-search-trigger"
        onClick={() => { setOpen(true); setQuery(''); }}
        className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-[#526071] hover:border-[#003366] hover:text-[#003366] transition-colors bg-white"
      >
        <Search className="w-3.5 h-3.5" />
        <span>Zoeken...</span>
        <kbd className="ml-4 text-[10px] font-mono bg-gray-100 px-1.5 py-0.5 rounded">\u2318K</kbd>
      </button>

      {/* Search dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" aria-describedby="search-desc">
          <div className="sr-only" id="search-desc">Zoek door alle pagina's en modules</div>
          <div className="flex items-center gap-3 p-4 border-b">
            <Search className="w-4 h-4 text-[#526071]" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Zoek pagina, module of functie..."
              className="flex-1 text-sm outline-none bg-transparent"
            />
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="max-h-[320px] overflow-y-auto p-2">
            {filtered.length === 0 ? (
              <div className="text-center py-8 text-sm text-[#526071]">Geen resultaten voor "{query}"</div>
            ) : (
              filtered.map((page) => (
                <button
                  key={page.key}
                  onClick={() => handleSelect(page.key)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-50 text-left transition-colors group"
                >
                  <div>
                    <p className="text-sm font-medium text-[#0b1220]">{page.label}</p>
                    <p className="text-xs text-[#526071]">{page.desc}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#003366] transition-colors" />
                </button>
              ))
            )}
          </div>
          <div className="p-3 border-t bg-gray-50 flex items-center justify-between text-xs text-[#526071]">
            <span>Navigeer met pijltjestoetsen</span>
            <span><kbd className="font-mono bg-white px-1.5 py-0.5 rounded border text-[10px]">Esc</kbd> om te sluiten</span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
